import React from 'react';
import { Box, Typography, CircularProgress, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';

const CircularProgressWithLabel = styled(Box)(() => ({
  position: 'relative',
  display: 'inline-flex'
}));

const CircularProgressBackground = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.action.disabledBackground
}));

const CircularProgressForeground = styled(CircularProgress)(() => ({
  position: 'absolute',
  left: 0,
  [`& .MuiCircularProgress-circle`]: {
    strokeLinecap: 'round'
  }
}));

type CompactProgressProps = {
  completed: number;
  total: number;
  size?: number;
  thickness?: number;
};

export const CompactProgress: React.FC<CompactProgressProps> = ({
  completed,
  total,
  size = 40,
  thickness = 4
}) => {
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isComplete = completed === total && total > 0;

  const getColor = () => {
    if (isComplete) return 'success';
    if (percentage >= 75) return 'primary';
    if (percentage >= 50) return 'info';
    return 'warning';
  };

  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <CircularProgressWithLabel>
        <CircularProgressBackground
          variant="determinate"
          value={100}
          size={size}
          thickness={thickness}
        />
        <CircularProgressForeground
          variant="determinate"
          value={percentage}
          size={size}
          thickness={thickness}
          color={getColor()}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography
            variant="caption"
            component="div"
            color="text.primary"
            sx={{ fontWeight: 600, fontSize: '0.65rem' }}
          >
            {`${completed}/${total}`}
          </Typography>
        </Box>
      </CircularProgressWithLabel>
      <Box>
        <Typography
          variant="body2"
          color="text.primary"
          sx={{ fontWeight: 500 }}
        >
          {percentage}% Complete
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {completed} of {total} tasks done
        </Typography>
      </Box>
    </Stack>
  );
};
